import {
  getPaginationRowModel,
  getFilteredRowModel,
  RowSelectionState,
  ColumnDef,
} from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import { CertificateCategorySchema } from "@tfg/types/schemas/certificates"

import {
  HeaderControlsProvider,
  useHeaderControlsContext,
} from "~/components/contexts/useHeaderControlsContext"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import {
  CertificateByOrganizationDto,
  FindCertificatesByOrganizationIdQueryResponse,
  useFindCertificatesByOrganizationId,
} from "~/gen/api"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { CertificatesTable } from "~/pages/Brand/Certificates/components/CertificatesTable"
import {
  CertificatesListView,
  CertificatesListViewSchema,
} from "~/pages/Brand/Certificates/constants"
import { useColumns } from "~/pages/Brand/Certificates/hooks/columns"
import {
  CertificatesTableContext,
  defaultContext,
} from "~/pages/Brand/Certificates/provider/CertificatesTableContext"
import { useCertificateDeepLink } from "~/pages/Brand/Certificates/hooks/use-certificate-deep-link"

const CertificatesTableProviderContent = () => {
  const [selectedCertificateInfo, setSelectedCertificateInfo] =
    useState<CertificateByOrganizationDto | null>(null)
  const handleCertificateDeepLinkResolved = useCallback(
    (certificate: CertificateByOrganizationDto) => {
      setSelectedCertificateInfo(certificate)
    },
    []
  )
  useCertificateDeepLink(handleCertificateDeepLinkResolved)
  const params = useParams()

  const view =
    CertificatesListViewSchema.safeParse(params.view).data ??
    CertificatesListView.ALL
  const category =
    CertificateCategorySchema.safeParse(view.toLocaleUpperCase()).data ??
    undefined

  const { sorts, setSorts, filters, setFilters, reset } =
    useHeaderControlsContext()
  const [search, setSearch] = useState<string>("")
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const filtersValue = useMemo(
    () => ({
      ...filters,
      ...(category ? { category } : {}),
    }),
    [filters, category]
  )
  const {
    data,
    isPending: isLoading,
    isError,
    error,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindCertificatesByOrganizationId,
    FindCertificatesByOrganizationIdQueryResponse
  >(useFindCertificatesByOrganizationId, {
    filters: filtersValue,
    search,
    sorts,
  })
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  useEffect(() => {
    if (selectedCertificateInfo?.id) {
      const certificate = data?.data.find(
        (certificate) => certificate.id === selectedCertificateInfo.id
      )
      if (certificate) {
        setSelectedCertificateInfo(certificate)
      }
    }
  }, [data, selectedCertificateInfo?.id])

  const columns = useColumns()
  const table = useCustomReactTable({
    data: data?.data ?? [],
    columns,
    initialColumnPinning: {
      left: ["type_category", "type_name", "name"],
    },
    state: {
      pagination: pageBasedPagination,
      rowSelection,
    },
    manualPagination: true,
    onPaginationChange: setPageBasedPagination,
    pageCount: totalPages,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <CertificatesTableContext.Provider
      value={{
        ...defaultContext,
        view,
        filters,
        data: data ?? {
          data: [],
          count: 0,
          take: 0,
          skip: 0,
        },
        totalCount: data?.count ?? 0,
        isError,
        error,
        isLoading,
        selectedCertificateInfo,
        setSelectedCertificateInfo,
        setFilters,
        unsetFilter: reset,
        rowSelection,
        setRowSelection,
        sorts,
        setSorts,
        pagination: pageBasedPagination,
        setPagination: setPageBasedPagination,
        search,
        setSearch,
        columns: columns as ColumnDef<CertificateByOrganizationDto>[],
        table,
        isColumnManagerOpen,
        setIsColumnManagerOpen,
      }}
    >
      <CertificatesTable />
    </CertificatesTableContext.Provider>
  )
}

export const CertificatesTableProvider = () => {
  return (
    <HeaderControlsProvider>
      <CertificatesTableProviderContent />
    </HeaderControlsProvider>
  )
}
